import React, { useMemo } from "react";
import { useParams, useRouteMatch } from "react-router-dom";
import { Button, Card, FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import RemediationCompletionScreen from "../../components/landingscreens/RemediationCompletionScreen";
import { RemediationConfirmationScreen } from "../../components/landingscreens/RemediationConfirmationScreen";
import {
  ComplianceCaseAction,
  ComplianceCaseAuditTagType,
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
} from "../../graphql/supergraphEnums";
import {
  useGetUserComplianceCasesQuery,
  usePerformActionMutation,
} from "../../graphql/supergraphHooks";
import { useLPComplianceUXv2 } from "../../hooks/featureFlags";
import { convertTimestampToDate } from "../../utils/date_formatter";
import { clientContext } from "../../utils/graphql_utils";
import { StandAloneCompliance } from "../StandAloneCompliance";
import { buildComponentContext } from "../StandAloneCompliance/utils";
import { LandingUIState } from "../types";
import { navigationButton, contentWrapper } from "./styles_vanilla.css";
import { findRemediationLoadingState } from "./utils";

export const ComplianceRemediation = ({
  arenaId,
  userFullName,
  errorComponent,
}: {
  arenaId: number;
  userFullName?: string;
  errorComponent: React.JSX.Element;
}) => {
  const { positionId } = useParams<{ positionId: string }>();

  const { url: basePath } = useRouteMatch();

  const unleashLPUXv2 = useLPComplianceUXv2();

  const result = useGetUserComplianceCasesQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseType: ComplianceCaseTypeEnum.Remediation,
      externalObject: {
        objectTypeEnum: ComplianceExternalObjectType.Position,
        objectId: positionId,
        service: "main",
      },
      auditTag: ComplianceCaseAuditTagType.CaseSubmitted,
    },
  });

  const [performAction, { loading: performActionLoading }] = usePerformActionMutation({
    context: clientContext(),
    refetchQueries: ["GetUserComplianceCases"],
    awaitRefetchQueries: true,
  });

  const complianceCase = result.data?.userAccessibleComplianceCases[0];

  const investmentPosition = complianceCase?.position;
  const investingAccount = investmentPosition?.account?.name;
  const submittedAt: string | null =
    complianceCase?.events[0]?.tag === ComplianceCaseAuditTagType.CaseSubmitted
      ? complianceCase?.events[0]?.createdAt || null
      : null;

  const remediationLoadingState = useMemo(() => {
    return findRemediationLoadingState(performActionLoading, complianceCase);
  }, [complianceCase, performActionLoading]);

  const startCompliance = () => {
    void performAction({
      variables: {
        caseId: Number(complianceCase?.id),
        action: ComplianceCaseAction.StartSubmission,
      },
    });
  };

  const submitCompliance = () => {
    void performAction({
      variables: {
        caseId: Number(complianceCase?.id),
        action: ComplianceCaseAction.CompleteSubmission,
      },
    });
  };

  const goToPortalHome = () => {
    window.location.href = "/lp/home";
  };

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        leftActionContent={
          <Button
            variant="neutral"
            icon="xmark"
            iconPosition="left"
            iconVariant="outline"
            className={navigationButton}
            onClick={() => {
              goToPortalHome();
            }}
          >
            Exit
          </Button>
        }
        title={"Identification"}
        rightActionContent={
          <Icon
            type="question-circle"
            variant="outline"
            className={navigationButton}
            onClick={() => {
              window.open("https://investor-help.junipersquare.com/hc/en-us", "_blank");
            }}
          />
        }
      />
      <QueryLoader result={result} renderError={() => errorComponent}>
        <FullscreenTakeover.Body>
          {remediationLoadingState === LandingUIState.CaseNotFound ? (
            errorComponent
          ) : (
            <Card className={contentWrapper}>
              {remediationLoadingState === LandingUIState.Compliance && (
                <StandAloneCompliance
                  componentContext={buildComponentContext(arenaId, basePath, unleashLPUXv2, {
                    id: complianceCase?.id,
                    caseType: complianceCase?.caseType,
                    investingProfileId: complianceCase?.investingProfile?.id,
                    programId: complianceCase?.programId,
                  })}
                  onStepCompletion={() => {
                    submitCompliance();
                  }}
                />
              )}
              {remediationLoadingState === LandingUIState.Completion && (
                <RemediationCompletionScreen
                  gpName={investmentPosition?.arena?.name}
                  informationProvidedBy={userFullName}
                  date={convertTimestampToDate(submittedAt)}
                  investingAccount={investingAccount ?? "the investing account"}
                  submittingResponse={performActionLoading}
                />
              )}
              {remediationLoadingState === LandingUIState.Confirmation && (
                <RemediationConfirmationScreen
                  investingAccount={investingAccount ?? "the investing account"}
                  submittingResponse={performActionLoading}
                  skipClickHandler={goToPortalHome}
                  continueClickHandler={() => {
                    startCompliance();
                  }}
                />
              )}
            </Card>
          )}
        </FullscreenTakeover.Body>
      </QueryLoader>
    </FullscreenTakeover>
  );
};
